/**
 * DemoInstructionPanel – Fixed side-by-side instruction panel for the demo flow.
 *
 * Shows the current step's title, narration text, progress, and a step list
 * synchronized with the active narration. Visible only while a demo script is
 * actively playing/paused (driven by DemoContext + DemoPlayerService).
 *
 * Designed to coexist with DemoControlBar (bottom-center) and DemoGuideOverlay
 * (the dimmed highlight tooltip) — this panel is the persistent "what we are
 * doing now" reference rail on the right.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Play, Pause, Square, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/modules/shared/components/ui/button';
import { Badge } from '@/modules/shared/components/ui/badge';
import { useDemo } from '@/modules/shared/contexts/DemoContext';
import { useDemoAccess } from '@/modules/shared/hooks/useDemoAccess';
import { DEMO_SCRIPTS, type DemoScript, type DemoScriptStep } from '@/modules/shared/services/DemoScripts';
import { demoPlayer } from '@/modules/shared/services/DemoPlayerService';
import { demoNarration, type NarrationState } from '@/modules/shared/services/DemoNarrationService';

export const DEMO_PANEL_WIDTH = 360;

export function DemoInstructionPanel() {
  const demo = useDemo();
  const { isDemoAllowed } = useDemoAccess();
  const [narrationState, setNarrationState] = useState<NarrationState>(demoNarration.getState());

  useEffect(() => demoNarration.subscribe(setNarrationState), []);

  const activeScript: DemoScript | null = useMemo(
    () => DEMO_SCRIPTS.find((s) => s.id === demo.activeFlowId) ?? null,
    [demo.activeFlowId],
  );

  if (!isDemoAllowed) return null;
  if (!activeScript || (!demo.isPlaying && !demo.isPaused)) return null;

  const currentStep: DemoScriptStep | undefined = activeScript.steps[demo.currentStep];
  const progressPct = ((demo.currentStep + 1) / Math.max(activeScript.steps.length, 1)) * 100;
  const muted = narrationState === 'muted';

  return (
    <aside
      className="fixed right-0 top-0 z-[9985] flex h-screen flex-col border-l border-border bg-background shadow-xl"
      style={{ width: DEMO_PANEL_WIDTH }}
      data-demo-panel="true"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[11px]">
              Demo
            </Badge>
            <span className="text-[11px] text-muted-foreground">
              Step {demo.currentStep + 1} of {activeScript.steps.length}
            </span>
          </div>
          <h2 className="mt-1 truncate text-[13px] font-semibold text-foreground" title={activeScript.name}>
            {activeScript.name}
          </h2>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => {
            demo.stop();
            demoPlayer.stop();
            demoNarration.stop();
          }}
          title="Stop demo"
        >
          <Square className="h-3 w-3" />
        </Button>
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-3">
        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Current step body */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {currentStep ? (
          <>
            <div className="flex items-center gap-2">
              {currentStep.badge && (
                <Badge variant="secondary" className="text-[11px]">
                  {currentStep.badge}
                </Badge>
              )}
              {narrationState === 'speaking' && (
                <span className="flex items-center gap-1 text-[11px] text-primary">
                  <Volume2 className="h-3 w-3" />
                  Narrating
                </span>
              )}
            </div>

            <h3 className="mt-2 text-[14px] font-semibold text-foreground">{currentStep.title}</h3>
            <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
              {renderNarrationText(currentStep.narration ?? currentStep.description)}
            </p>

            {/* Step list */}
            <div className="mt-5">
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Demo flow
              </div>
              <ol className="space-y-1">
                {activeScript.steps.map((step, idx) => {
                  const isCurrent = idx === demo.currentStep;
                  const isComplete = idx < demo.currentStep;
                  return (
                    <li
                      key={step.id}
                      className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-[11px] transition-colors ${
                        isCurrent
                          ? 'bg-primary/10 text-foreground'
                          : isComplete
                            ? 'text-muted-foreground'
                            : 'text-muted-foreground/70'
                      }`}
                    >
                      <span
                        className={`mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full text-[11px] font-semibold ${
                          isCurrent
                            ? 'bg-primary text-primary-foreground'
                            : isComplete
                              ? 'bg-primary/30 text-primary'
                              : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span className="truncate">{step.title}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          </>
        ) : (
          <p className="text-[12px] text-muted-foreground">Demo finished.</p>
        )}
      </div>

      {/* Footer controls */}
      <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => demoPlayer.previousStep()}
            disabled={demo.currentStep === 0}
            title="Previous step"
          >
            <ChevronLeft className="h-3 w-3" />
          </Button>
          {demo.isPlaying ? (
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                demo.pause();
                demoPlayer.pause();
                demoNarration.pause();
              }}
              title="Pause"
            >
              <Pause className="h-3 w-3" />
            </Button>
          ) : (
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                demo.resume();
                demoPlayer.resume();
                demoNarration.resume();
              }}
              title="Resume"
            >
              <Play className="h-3 w-3" />
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => demoPlayer.nextStep()}
            title="Next step"
          >
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>

        {demoNarration.isSupported() && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => demoNarration.toggleMute()}
            title={muted ? 'Unmute narration' : 'Mute narration'}
          >
            {muted ? (
              <VolumeX className="h-3 w-3 text-muted-foreground" />
            ) : (
              <Volume2 className="h-3 w-3 text-primary" />
            )}
          </Button>
        )}
      </div>
    </aside>
  );
}

/**
 * Render narration text with [em]…[/em] and [slow]…[/slow] tokens converted
 * to inline emphasis. Strips [pause=NNN] and [break] tokens entirely (they
 * only matter for speech timing).
 */
function renderNarrationText(text: string): ReactNode {
  const cleaned = text.replace(/\[pause=\d+\]/gi, '').replace(/\[break\]/gi, '');
  const parts: ReactNode[] = [];
  const regex = /\[em\](.*?)\[\/em\]|\[slow\](.*?)\[\/slow\]/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(cleaned)) !== null) {
    if (match.index > lastIndex) {
      parts.push(cleaned.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      parts.push(
        <strong key={`em-${key++}`} className="font-semibold text-foreground">
          {match[1]}
        </strong>,
      );
    } else if (match[2] !== undefined) {
      parts.push(
        <em key={`slow-${key++}`} className="italic text-foreground/80">
          {match[2]}
        </em>,
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < cleaned.length) parts.push(cleaned.slice(lastIndex));
  return parts.length ? parts : cleaned;
}

